// Página de Visitas: onde se controla quem visitou o quê, e com que clientes.
// Mostra os totais (realizadas, clientes distintos, média por cliente), as listas por estado
// e uma vista agrupada por cliente (5 visitas ao mesmo cliente vs 5 clientes diferentes).
import { useState, useMemo } from "react";
import { Calendar, CheckCircle2, AlertTriangle, XCircle, Users, Search, ChevronDown, ChevronRight, Home, Plus } from "lucide-react";
import { paleta } from "./tokens";
import { hojeISO } from "./datas";

const GRADIENTE = "linear-gradient(90deg, #0B1B3D 0%, #1E3566 45%, #4A6BA8 100%)";
const CINZA_ACT = "#6B7385";

const estadoDe = (v, hoje) => {
  if (v.estado === "realizada") return "realizada";
  if (v.estado === "cancelada" || v.estado === "nao_realizada") return "cancelada";
  if (v.data && v.data < hoje) return "atrasada";
  return "agendada";
};
const ROTULO = { realizada: "Realizada", cancelada: "Cancelada", atrasada: "Atrasada", agendada: "Agendada" };
const dataPT = (d) => (d ? new Date(d).toLocaleDateString("pt-PT") : "—");
const nomeLead = (l) => (l ? l.nome_completo || l.nome || "—" : "—");

function Cartao({ Icone, titulo, valor, nota, onClick, ativo }) {
  return (
    <button onClick={onClick} aria-pressed={!!ativo} style={{ textAlign: "left", background: "#fff", border: "1px solid " + (ativo ? paleta.navy700 : paleta.cinza200), borderTop: "3px solid " + (ativo ? paleta.navy700 : paleta.cinza200), borderRadius: 12, padding: "12px 14px", cursor: onClick ? "pointer" : "default", minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12.5, color: paleta.cinza600, fontWeight: 500 }}><Icone size={15} aria-hidden="true" />{titulo}</div>
      <div style={{ fontSize: 26, fontWeight: 700, color: paleta.navy900, marginTop: 2 }}>{valor}</div>
      {nota && <div style={{ fontSize: 12, color: paleta.cinza600 }}>{nota}</div>}
    </button>
  );
}

function Pilula({ ativo, onClick, children }) {
  return (
    <button onClick={onClick} aria-pressed={ativo} style={{ background: ativo ? CINZA_ACT : "#fff", color: ativo ? "#fff" : paleta.navy900, border: "1px solid " + (ativo ? CINZA_ACT : paleta.cinza200), borderRadius: 99, padding: "6px 14px", fontSize: 13, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>{children}</button>
  );
}

function LinhaVisita({ v, lead, estado, mostrarCliente, onAbrir }) {
  const cor = estado === "realizada" ? paleta.sucesso : estado === "agendada" ? paleta.navy700 : estado === "atrasada" ? paleta.aviso : paleta.cinza400;
  const Icone = estado === "realizada" ? CheckCircle2 : estado === "cancelada" ? XCircle : estado === "atrasada" ? AlertTriangle : Calendar;
  const imovel = [v.tipologia, v.tipo_imovel, v.zona].filter(Boolean).join(" · ") + (v.preco ? ` · ${Number(v.preco).toLocaleString("pt-PT")} €` : "");
  return (
    <button onClick={() => onAbrir(v, lead)} style={{ width: "100%", textAlign: "left", background: "#fff", border: "none", borderBottom: "1px solid " + paleta.navy100, padding: "11px 14px", display: "flex", alignItems: "center", gap: 12, cursor: "pointer" }}>
      <Icone size={18} aria-hidden="true" style={{ color: cor, flexShrink: 0 }} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {mostrarCliente && <span style={{ fontWeight: 600, fontSize: 14, color: paleta.cinza900 }}>{nomeLead(lead)}</span>}
          <span style={{ fontSize: 12, fontWeight: 600, color: cor, background: "#F4F6FA", border: "1px solid " + paleta.navy100, borderRadius: 99, padding: "1px 9px" }}>{ROTULO[estado]}</span>
        </div>
        <div style={{ fontSize: 13, color: paleta.cinza600, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {imovel || "Imóvel sem detalhes"}{v.id_imovel ? ` · Ref ${v.id_imovel}` : ""}{v.consultor_nome ? ` · ${v.consultor_nome}` : ""}
        </div>
        {estado === "realizada" && (v.gostou === true || v.gostou === false || v.quer_proposta) && (
          <div style={{ fontSize: 12, color: paleta.cinza600, marginTop: 2 }}>{v.gostou === true ? "Gostou" : v.gostou === false ? "Não gostou" : ""}{v.quer_proposta ? `${v.gostou === true || v.gostou === false ? " · " : ""}quer proposta` : ""}</div>
        )}
      </div>
      <div style={{ textAlign: "right", flexShrink: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 600, color: paleta.navy900 }}>{dataPT(v.data)}</div>
        <div style={{ fontSize: 12, color: paleta.cinza400 }}>{v.hora || ""}</div>
      </div>
      <ChevronRight size={16} aria-hidden="true" style={{ color: paleta.cinza400, flexShrink: 0 }} />
    </button>
  );
}

export default function PainelVisitas({ visitas = [], leads = [], isLider = false, onAbrirVisita, onNova }) {
  const hoje = hojeISO();
  const [estado, setEstado] = useState("todas");
  const [vista, setVista] = useState("visitas"); // "visitas" | "clientes"
  const [periodo, setPeriodo] = useState("todo");
  const [consultor, setConsultor] = useState("");
  const [pesquisa, setPesquisa] = useState("");
  const [abertos, setAbertos] = useState({});

  const leadPorId = useMemo(() => { const m = {}; (leads || []).forEach(l => { m[String(l.id)] = l; }); return m; }, [leads]);

  const ano = new Date().getFullYear();
  const mes = new Date().getMonth();
  const noPeriodo = (d) => {
    if (periodo === "todo") return true;
    if (!d) return false;
    const x = new Date(d);
    if (periodo === "mes") return x.getFullYear() === ano && x.getMonth() === mes;
    if (periodo === "mesAnt") { const a = mes === 0 ? ano - 1 : ano; const m = mes === 0 ? 11 : mes - 1; return x.getFullYear() === a && x.getMonth() === m; }
    if (periodo === "ano") return x.getFullYear() === ano;
    return true;
  };

  const consultores = useMemo(() => Array.from(new Set((visitas || []).map(v => v.consultor_nome).filter(Boolean))).sort((a, b) => a.localeCompare(b, "pt")), [visitas]);

  const base = useMemo(() => {
    const q = pesquisa.trim().toLowerCase();
    return (visitas || []).filter(v => {
      if (!noPeriodo(v.data)) return false;
      if (consultor && v.consultor_nome !== consultor) return false;
      if (q) {
        const lead = leadPorId[String(v.lead_id)];
        const alvo = [nomeLead(lead), v.id_imovel, v.zona, v.imovel_externo_morada].filter(Boolean).join(" ").toLowerCase();
        if (!alvo.includes(q)) return false;
      }
      return true;
    }).map(v => ({ v, e: estadoDe(v, hoje) }));
  }, [visitas, periodo, consultor, pesquisa, leadPorId, hoje]); // eslint-disable-line react-hooks/exhaustive-deps

  const contagem = useMemo(() => {
    const c = { todas: base.length, agendada: 0, atrasada: 0, realizada: 0, cancelada: 0 };
    base.forEach(x => { c[x.e] += 1; });
    return c;
  }, [base]);

  const realizadas = base.filter(x => x.e === "realizada");
  const clientesRealizadas = new Set(realizadas.map(x => String(x.v.lead_id)));
  const media = clientesRealizadas.size ? (realizadas.length / clientesRealizadas.size) : 0;
  const clientesRepetidos = (() => { const m = {}; realizadas.forEach(x => { const k = String(x.v.lead_id); m[k] = (m[k] || 0) + 1; }); return Object.values(m).filter(n => n > 1).length; })();

  const filtradas = base
    .filter(x => estado === "todas" || x.e === estado)
    .sort((a, b) => ((b.v.data || "") + (b.v.hora || "")).localeCompare((a.v.data || "") + (a.v.hora || "")));

  // Agrupado por cliente
  const grupos = useMemo(() => {
    const m = {};
    filtradas.forEach(x => {
      const k = String(x.v.lead_id);
      if (!m[k]) m[k] = { id: k, lead: leadPorId[k], itens: [] };
      m[k].itens.push(x);
    });
    return Object.values(m).sort((a, b) => b.itens.length - a.itens.length || nomeLead(a.lead).localeCompare(nomeLead(b.lead), "pt"));
  }, [filtradas, leadPorId]);

  const TABS = [["todas", "Todas"], ["agendada", "Agendadas"], ["atrasada", "Atrasadas"], ["realizada", "Realizadas"], ["cancelada", "Canceladas"]];
  const select = { padding: "7px 10px", border: "1px solid " + paleta.cinza200, borderRadius: 8, fontSize: 13.5, background: "#fff", color: paleta.cinza900, fontFamily: "inherit" };

  return (
    <div>
      <div style={{ marginBottom: 14, display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div>
        <h1 style={{ fontSize: 26, fontWeight: 700, color: paleta.navy900, margin: 0 }}>Visitas</h1>
        <div style={{ fontSize: 13.5, color: paleta.cinza600, marginTop: 2 }}>Quem visitou o quê, e com que clientes. {isLider ? "Vês as visitas de toda a equipa." : "Vês as tuas visitas."}</div>
        </div>
        {onNova && <button onClick={onNova} style={{ background: "#1E3566", color: "#fff", border: "1px solid #1E3566", borderRadius: 8, padding: "9px 18px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 8 }}><Plus size={16} aria-hidden="true" />Nova visita</button>}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(170px, 1fr))", gap: 10, marginBottom: 14 }}>
        <Cartao Icone={Calendar} titulo="Agendadas" valor={contagem.agendada} nota="por realizar" ativo={estado === "agendada"} onClick={() => setEstado(estado === "agendada" ? "todas" : "agendada")} />
        <Cartao Icone={AlertTriangle} titulo="Atrasadas" valor={contagem.atrasada} nota="data passada sem resultado" ativo={estado === "atrasada"} onClick={() => setEstado(estado === "atrasada" ? "todas" : "atrasada")} />
        <Cartao Icone={CheckCircle2} titulo="Realizadas" valor={contagem.realizada} nota={`com ${clientesRealizadas.size} ${clientesRealizadas.size === 1 ? "cliente" : "clientes"}`} ativo={estado === "realizada"} onClick={() => setEstado(estado === "realizada" ? "todas" : "realizada")} />
        <Cartao Icone={Users} titulo="Média por cliente" valor={media ? media.toLocaleString("pt-PT", { maximumFractionDigits: 1 }) : "0"} nota={`${clientesRepetidos} ${clientesRepetidos === 1 ? "cliente com" : "clientes com"} mais de 1 visita`} />
      </div>

      <div style={{ background: "#fff", border: "1px solid " + paleta.cinza200, borderRadius: 12, overflow: "hidden" }}>
        <div style={{ background: GRADIENTE, color: "#fff", padding: "10px 16px", fontSize: 15.5, fontWeight: 600, display: "flex", alignItems: "center", gap: 8 }}>
          <Home size={17} aria-hidden="true" />{vista === "visitas" ? `Visitas (${filtradas.length})` : `Clientes (${grupos.length}) · ${filtradas.length} visitas`}
        </div>
        <div style={{ padding: "12px 14px", display: "flex", flexDirection: "column", gap: 10, borderBottom: "1px solid " + paleta.navy100 }}>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {TABS.map(([k, l]) => <Pilula key={k} ativo={estado === k} onClick={() => setEstado(k)}>{l} ({k === "todas" ? contagem.todas : contagem[k]})</Pilula>)}
          </div>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ position: "relative", flex: "1 1 200px", minWidth: 180 }}>
              <Search size={15} aria-hidden="true" style={{ position: "absolute", left: 10, top: 10, color: paleta.cinza400 }} />
              <input value={pesquisa} onChange={e => setPesquisa(e.target.value)} placeholder="Pesquisar cliente, referência ou zona" aria-label="Pesquisar visitas" style={{ ...select, width: "100%", paddingLeft: 32, boxSizing: "border-box" }} />
            </div>
            <select value={periodo} onChange={e => setPeriodo(e.target.value)} aria-label="Período" style={select}>
              <option value="todo">Todo o período</option><option value="mes">Este mês</option><option value="mesAnt">Mês passado</option><option value="ano">Este ano</option>
            </select>
            {isLider && consultores.length > 0 && (
              <select value={consultor} onChange={e => setConsultor(e.target.value)} aria-label="Consultor" style={select}>
                <option value="">Todos os consultores</option>{consultores.map(c => <option key={c} value={c}>{c}</option>)}
              </select>
            )}
            <div style={{ display: "flex", gap: 6 }}>
              <Pilula ativo={vista === "visitas"} onClick={() => setVista("visitas")}>Por visita</Pilula>
              <Pilula ativo={vista === "clientes"} onClick={() => setVista("clientes")}>Por cliente</Pilula>
            </div>
          </div>
        </div>

        {filtradas.length === 0 ? (
          <div style={{ textAlign: "center", padding: 28, color: paleta.cinza400, fontSize: 14 }}>Nenhuma visita neste filtro.</div>
        ) : vista === "visitas" ? (
          filtradas.map(x => <LinhaVisita key={x.v.id} v={x.v} lead={leadPorId[String(x.v.lead_id)]} estado={x.e} mostrarCliente onAbrir={onAbrirVisita} />)
        ) : (
          grupos.map(g => {
            const aberto = !!abertos[g.id];
            const nReal = g.itens.filter(x => x.e === "realizada").length;
            const Seta = aberto ? ChevronDown : ChevronRight;
            return (
              <div key={g.id}>
                <button onClick={() => setAbertos(a => ({ ...a, [g.id]: !a[g.id] }))} aria-expanded={aberto} style={{ width: "100%", textAlign: "left", background: aberto ? "#F4F6FA" : "#fff", border: "none", borderBottom: "1px solid " + paleta.navy100, padding: "11px 14px", display: "flex", alignItems: "center", gap: 10, cursor: "pointer" }}>
                  <Seta size={16} aria-hidden="true" style={{ color: paleta.cinza600, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontWeight: 600, fontSize: 14, color: paleta.cinza900 }}>{nomeLead(g.lead)}</span>
                  <span style={{ fontSize: 13, color: paleta.cinza600 }}>{g.itens.length} {g.itens.length === 1 ? "visita" : "visitas"}{nReal ? ` · ${nReal} ${nReal === 1 ? "realizada" : "realizadas"}` : ""}</span>
                </button>
                {aberto && <div style={{ borderLeft: "3px solid " + paleta.navy100 }}>{g.itens.map(x => <LinhaVisita key={x.v.id} v={x.v} lead={g.lead} estado={x.e} mostrarCliente={false} onAbrir={onAbrirVisita} />)}</div>}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
